import { useState } from 'react';
import { Copy, DownloadSimple, PaperPlaneTilt, SpinnerGap } from '@phosphor-icons/react';
import Breadcrumbs from '../components/Breadcrumbs';
import AppNav from '../components/AppNav';
import AppFooter from '../components/AppFooter';
import PageMeta from '../components/PageMeta';
import { auth } from '../config/firebase';
import type { AdvocacyLetterContent } from '@researcher/shared';
import { apiUrl } from '../config/api';
import { reportError } from '../utils/errorReporting';

const LETTER_TYPES = [
  { value: 'insurance_appeal', label: 'Insurance Appeal', description: 'Appeal a denied claim or request coverage for a treatment' },
  { value: 'employer_accommodation', label: 'Employer Accommodation', description: 'Request workplace accommodations under the ADA' },
  { value: 'provider_referral', label: 'Provider Referral Request', description: 'Request a referral to a specialist from your primary care provider' },
];

const TONES = ['professional', 'assertive', 'empathetic'];

export default function AdvocacyLetterGenerator() {
  const [letterType, setLetterType] = useState('insurance_appeal');
  const [condition, setCondition] = useState('');
  const [context, setContext] = useState('');
  const [tone, setTone] = useState('professional');
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState<AdvocacyLetterContent | null>(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const handleGenerate = async () => {
    if (!context.trim() || context.trim().length < 10) {
      setError('Please provide more details about your situation (at least 10 characters).');
      return;
    }

    setGenerating(true);
    setError('');
    setResult(null);

    try {
      const headers: HeadersInit = { 'Content-Type': 'application/json' };
      const user = auth.currentUser;
      if (user) {
        const token = await user.getIdToken();
        headers['Authorization'] = `Bearer ${token}`;
      } else {
        setError('Please sign in to generate a letter.');
        setGenerating(false);
        return;
      }

      const fullContext = `Letter type: ${letterType}\nCondition: ${condition}\nTone: ${tone}\n\nSituation: ${context}`;

      const response = await fetch(apiUrl('/artifacts/generate'), {
        method: 'POST',
        headers,
        body: JSON.stringify({ type: 'advocacy_letter', context: fullContext }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to generate letter');

      setResult(data.data.content as AdvocacyLetterContent);
    } catch (err: any) {
      setError(reportError(err, 'generation'));
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = async () => {
    if (!result) return;
    const text = `Subject: ${result.subject}\n\n${result.body}`;
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    if (!result) return;
    const text = `Subject: ${result.subject}\n\n${result.body}\n\nKey Points:\n${result.keyPoints.map(p => `- ${p}`).join('\n')}`;
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `advocacy-letter-${letterType}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white dark:from-gray-900 dark:to-gray-900">
      <PageMeta
        title="Advocacy Letter Generator"
        description="Generate insurance appeal letters, employer accommodation requests, and provider referral letters for women's health conditions."
        path="/advocacy/letter-generator"
      />
      <AppNav />
      <main className="pt-16 pb-16 md:pb-0 min-h-screen">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 py-12">
          <Breadcrumbs current="Letter Generator" />

          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 dark:text-gray-100 mb-2">Advocacy Letter Generator</h1>
          <p className="text-gray-600 dark:text-gray-400 mb-8">Generate professional letters to advocate for your healthcare needs.</p>

          {/* Form */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 mb-8 space-y-6">
            {/* Letter type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Letter Type</label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {LETTER_TYPES.map(lt => (
                  <button
                    key={lt.value}
                    onClick={() => setLetterType(lt.value)}
                    className={`text-left p-3 rounded-lg border-2 transition-colors ${
                      letterType === lt.value
                        ? 'border-pink-500 bg-pink-50 dark:bg-pink-950'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <div className="text-sm font-medium text-gray-800 dark:text-gray-100">{lt.label}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{lt.description}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Condition */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Condition</label>
              <input
                type="text"
                value={condition}
                onChange={e => setCondition(e.target.value)}
                placeholder="e.g., Endometriosis, PCOS, PMDD"
                className="w-full px-4 py-2.5 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500 text-gray-800 dark:text-gray-100 placeholder:text-gray-400"
              />
            </div>

            {/* Context */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Your Situation</label>
              <textarea
                value={context}
                onChange={e => setContext(e.target.value)}
                rows={5}
                placeholder="Describe your situation in detail. For insurance appeals: what was denied and why. For accommodations: what you need and why. For referrals: why you need a specialist."
                className="w-full px-4 py-2.5 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500 text-gray-800 dark:text-gray-100 placeholder:text-gray-400 resize-none"
              />
            </div>

            {/* Tone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Tone</label>
              <div className="flex gap-3">
                {TONES.map(t => (
                  <button
                    key={t}
                    onClick={() => setTone(t)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium capitalize transition-colors ${
                      tone === t
                        ? 'bg-pink-500 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            {error && (
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-3">
                <p className="text-sm text-amber-800 dark:text-amber-200">{error}</p>
              </div>
            )}

            <button
              onClick={handleGenerate}
              disabled={generating}
              className="w-full py-3 bg-gradient-to-r from-orange-500 to-pink-500 text-white font-semibold rounded-lg hover:from-orange-600 hover:to-pink-600 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {generating ? (
                <>
                  <SpinnerGap size={20} className="animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <PaperPlaneTilt size={20} weight="fill" />
                  Generate Letter
                </>
              )}
            </button>
          </div>

          {/* Result */}
          {result && (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100">Your Letter</h2>
                <div className="flex gap-2">
                  <button
                    onClick={handleCopy}
                    className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center gap-1"
                  >
                    <Copy size={14} />
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                  <button
                    onClick={handleDownload}
                    className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center gap-1"
                  >
                    <DownloadSimple size={14} />
                    Download
                  </button>
                </div>
              </div>

              <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Subject: {result.subject}</p>
              </div>

              <div className="prose dark:prose-invert prose-sm max-w-none mb-6">
                <div className="whitespace-pre-wrap text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                  {result.body}
                </div>
              </div>

              {result.keyPoints.length > 0 && (
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Key Points</h3>
                  <ul className="space-y-1">
                    {result.keyPoints.map((point, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
                        <span className="text-pink-500 mt-0.5">•</span>
                        {point}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                <p className="text-xs text-yellow-700 dark:text-yellow-300">
                  Review the letter and fill in any [PLACEHOLDER] sections with your personal details before sending. This letter is AI-generated and should be reviewed for accuracy.
                </p>
              </div>
            </div>
          )}
        </div>
      </main>
      <AppFooter />
    </div>
  );
}
